import Rollbar from 'rollbar'
import type { Network } from '@/services/networks/networksApi'
import type { ApiUser } from '@/services/users/userApi'
import { apiEnvironment, isLocalhostUi } from '@/utils/environment'

const accessToken = import.meta.env.VITE_PUBLIC_ROLLBAR_TOKEN

const IGNORED_MESSAGES = [
  'transition superseded',
  'Possibly unhandled rejection: transition superseded',
  'Possibly unhandled rejection: backdrop click',
  'Possibly unhandled rejection: undefined',
  'Possibly unhandled rejection: escape key press',
]

const IGNORED_STATUS_CODES = new Set([401, 403, 404, 422])
const IGNORED_REJECTION_TYPES = new Set([2, 3, 5])

const SCRUB_FIELDS = [
  'refresh_token',
  'access_token',
  'auth_token',
  'Authorization',
  'authorization',
  'password',
  'passwd',
  'cookie',
  'set-cookie',
]

function errorMessage(args: Rollbar.LogArgument[], item: Rollbar.Dictionary): string {
  for (const arg of args) {
    if (typeof arg === 'string') return arg
    if (arg instanceof Error) return arg.message
    if (arg && typeof arg === 'object' && 'message' in arg && typeof arg.message === 'string') {
      return arg.message
    }
  }

  const body = item.body
  if (!body || typeof body !== 'object') return ''
  const message = (body as Rollbar.Dictionary).message
  if (message && typeof message === 'object' && typeof (message as Rollbar.Dictionary).body === 'string') {
    return (message as Rollbar.Dictionary).body as string
  }
  const trace = (body as Rollbar.Dictionary).trace
  if (trace && typeof trace === 'object') {
    const exception = (trace as Rollbar.Dictionary).exception
    if (exception && typeof exception === 'object' && typeof (exception as Rollbar.Dictionary).message === 'string') {
      return (exception as Rollbar.Dictionary).message as string
    }
  }
  return ''
}

function shouldIgnore(_isUncaught: boolean, args: Rollbar.LogArgument[], item: Rollbar.Dictionary): boolean {
  if (/superseded/i.test(errorMessage(args, item))) return true

  return args.some((arg) => {
    if (!arg || typeof arg !== 'object') return false
    const value = arg as Record<string, unknown>
    const detail = value.detail as Record<string, unknown> | undefined
    return (
      IGNORED_STATUS_CODES.has(Number(value.status)) ||
      detail?.status === 404 ||
      IGNORED_REJECTION_TYPES.has(Number(value.type))
    )
  })
}

export const rollbarEnabled = Boolean(accessToken) && apiEnvironment !== 'local' && !isLocalhostUi

export const rollbar = accessToken
  ? new Rollbar({
      accessToken,
      captureUncaught: true,
      captureUnhandledRejections: true,
      captureIp: 'anonymize',
      enabled: rollbarEnabled,
      ignoreDuplicateErrors: true,
      scrubFields: SCRUB_FIELDS,
      scrubHeaders: SCRUB_FIELDS,
      ignoredMessages: [...IGNORED_MESSAGES, /superseded/i],
      checkIgnore: shouldIgnore,
      payload: {
        environment: apiEnvironment,
        client: {
          javascript: {
            code_version: __GIT_SHA__,
            source_map_enabled: true,
          },
        },
      },
    })
  : null

export function configureRollbarPerson(user: ApiUser | null, network: Network | null): void {
  if (!rollbar) return
  if (!user) {
    rollbar.configure({ payload: { person: { id: null }, custom: {} } })
    return
  }

  rollbar.configure({
    payload: {
      person: {
        id: user.id,
        username: user.username || undefined,
        email: user.username || undefined,
      },
      custom: network
        ? {
            networkId: network.id,
            networkTagId: network.tagId,
            networkName: network.name,
          }
        : {},
    },
  })
}

export function reportRollbarError(error: unknown, context?: Record<string, unknown>): void {
  if (!rollbar) return
  const normalized = error instanceof Error ? error : new Error(String(error ?? 'Unknown error'))
  if (context) {
    rollbar.error(normalized.message, normalized, { extra: context })
  } else {
    rollbar.error(normalized.message, normalized)
  }
}
